import { useState } from "react";
import { toast } from "sonner";
import { LineChart, RefreshCw, Building2, Users, FileCheck2, Shield, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";

function currentYear() {
    return new Date().getFullYear();
}

function fmtMoney(v) {
    return `$${Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function ReporteConsejo() {
    const [year, setYear] = useState(currentYear());
    const [q, setQ] = useState("Q1");
    const [loading, setLoading] = useState(false);
    const [rep, setRep] = useState(null);

    const generar = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(
                `/gobierno/reporte-consejo?trimestre=${year}-${q}`,
            );
            setRep(data);
        } catch (err) {
            toast.error("No se pudo generar el reporte", {
                description: err?.response?.data?.detail,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-8 lg:p-10 space-y-6 max-w-[1400px]" data-testid="reporte-page">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Gobierno · Fase 14
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <LineChart className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Reporte al Consejo
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Consolidado trimestral. Datos calculados en tiempo real a partir de
                        bitácora, delegados, comité y matriz.
                    </p>
                </div>
                <div className="flex items-end gap-3">
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Año
                        </label>
                        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                            <SelectTrigger className="mt-1 w-[110px]" data-testid="rep-year">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {[currentYear() - 1, currentYear(), currentYear() + 1].map((y) => (
                                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Trimestre
                        </label>
                        <Select value={q} onValueChange={setQ}>
                            <SelectTrigger className="mt-1 w-[110px]" data-testid="rep-q">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Q1">Q1 (Ene-Mar)</SelectItem>
                                <SelectItem value="Q2">Q2 (Abr-Jun)</SelectItem>
                                <SelectItem value="Q3">Q3 (Jul-Sep)</SelectItem>
                                <SelectItem value="Q4">Q4 (Oct-Dic)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={generar} disabled={loading} data-testid="rep-generar">
                        {loading ? "Generando…" : (<><RefreshCw className="h-3.5 w-3.5 mr-2" />Generar</>)}
                    </Button>
                </div>
            </div>

            {rep && (
                <>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <KPI icon={Building2} label="Nuevos fideicomisos" value={rep.nuevos_fideicomisos} testid="kpi-nuevos-fid" />
                        <KPI icon={Users} label="Altas delegados" value={rep.delegados_altas} testid="kpi-altas-del" />
                        <KPI icon={Users} label="Bajas delegados" value={rep.delegados_bajas} testid="kpi-bajas-del" />
                        <KPI icon={FileCheck2} label="Dictámenes emitidos" value={rep.dictamenes_emitidos} testid="kpi-dictamenes" />
                        <KPI icon={Shield} label="Excepciones autorizadas" value={rep.excepciones_autorizadas} />
                        <KPI icon={AlertTriangle} label="Conflictos declarados" value={rep.conflictos_declarados} />
                        <KPI icon={Shield} label="Incidencias PLD" value={rep.incidencias_pld} testid="kpi-pld" />
                        <KPI icon={LineChart} label="Trimestre" value={rep.trimestre} isText />
                    </div>

                    <div className="kpi-card">
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                            Volumen por escalón de la matriz
                        </div>
                        <table className="trf-table" data-testid="volumen-table">
                            <thead>
                                <tr>
                                    <th>Escalón</th>
                                    <th className="text-right">Instrucciones ejecutadas</th>
                                    <th className="text-right">Monto total (MXN)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(rep.volumen_por_escalon || {}).map(([nombre, v]) => (
                                    <tr key={nombre} data-testid={`escalon-vol-${nombre}`}>
                                        <td className="font-medium capitalize">{nombre.replace(/_/g, " ")}</td>
                                        <td className="text-right tabular">{v.count}</td>
                                        <td className="text-right tabular">{fmtMoney(v.monto_total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="text-xs text-muted-foreground">
                        Período: {rep.periodo.desde} → {rep.periodo.hasta} · Generado: {rep.generado_at?.slice(0, 19).replace("T", " ")}
                    </div>
                </>
            )}

            {!rep && !loading && (
                <div className="text-center py-16 text-muted-foreground text-sm">
                    Selecciona año y trimestre y presiona <strong>Generar</strong>.
                </div>
            )}
        </div>
    );
}

function KPI({ icon: Icon, label, value, isText = false, testid }) {
    return (
        <div className="kpi-card" data-testid={testid}>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <Icon className="h-3.5 w-3.5" /> {label}
            </div>
            <div className={`kpi-value mt-2 ${isText ? "text-xl" : "text-3xl"}`}>
                {isText ? value : Number(value || 0).toLocaleString("es-MX")}
            </div>
        </div>
    );
}
